import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Box,
  Grid,
  TextField,
  MenuItem,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import athleteService from '../services/athleteService';
import SchoolStrength from './SchoolStrength';
import CountyStrength from './CountyStrength';
import { chartTextStyle } from './StrengthShared';

// dataviz 驗證過的分類色盤（相鄰配對合格，最多 8 條序列）
const SERIES_COLORS = [
  '#2a78d6', '#eb6834', '#1baf7a', '#eda100',
  '#e87ba4', '#008300', '#4a3aa7', '#e34948',
];

// 秒數轉顯示文字：230.35 -> 3:50.35
const formatSeconds = (value) => {
  if (value == null) return '-';
  if (value < 60) return Number(value).toFixed(2);
  const minutes = Math.floor(value / 60);
  const seconds = (value - minutes * 60).toFixed(2).padStart(5, '0');
  return `${minutes}:${seconds}`;
};

// 序列名稱拆成（組別, 分級）：'肢體障礙女子組腦性麻痺站立組T35級'
//   -> 組別 '肢體障礙女子組'、分級 '腦性麻痺站立組T35級'
// 一般賽事的組別（如 公開男生組）沒有分級，分級為空字串
const mainOf = (name) => {
  const idx = name.indexOf('組');
  return idx === -1 ? name : name.slice(0, idx + 1);
};
const clsOf = (name) => {
  const idx = name.indexOf('組');
  return idx === -1 ? '' : name.slice(idx + 1);
};

const EventTrend = ({ sports, gamesNames, onSearchAthlete }) => {
  const [sport, setSport] = useState('田徑');
  const [gamesName, setGamesName] = useState('');
  const [sportOptions, setSportOptions] = useState([]);
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState(null);
  const [mains, setMains] = useState([]); // 選取的組別
  const [clsSel, setClsSel] = useState([]); // 選取的分級
  const [metric, setMetric] = useState('best');
  const [direction, setDirection] = useState('lower');
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // 運動類別清單跟著賽事連動：選定賽事時只列該賽事有成績的運動
  useEffect(() => {
    if (!gamesName) {
      setSportOptions(sports);
      return;
    }
    let mounted = true;
    athleteService
      .getStatsSports(gamesName)
      .then((res) => {
        if (mounted && res.success) setSportOptions(res.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [gamesName, sports]);

  // 目前選的運動不在清單內時，自動切到第一個可選運動
  useEffect(() => {
    if (sportOptions.length > 0 && !sportOptions.includes(sport)) {
      setSport(sportOptions[0]);
    }
  }, [sportOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  // 換運動類別或賽事時載入項目清單
  useEffect(() => {
    if (!sport) return;
    let mounted = true;
    setEvents([]);
    setEvent(null);
    setSeries([]);
    athleteService
      .getStatsEvents(sport, gamesName || undefined)
      .then((res) => {
        if (mounted && res.success) {
          setEvents(res.data);
          // 預設帶入 100公尺（若存在），讓使用者一進來就看得到圖
          if (res.data.includes('100公尺')) setEvent('100公尺');
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [sport, gamesName]); // eslint-disable-line react-hooks/exhaustive-deps

  // 選定項目即自動查詢
  useEffect(() => {
    if (!sport || !event) return;
    let mounted = true;
    setLoading(true);
    setMessage('');
    athleteService
      .getEventTrend(sport, event, gamesName || undefined)
      .then((res) => {
        if (!mounted) return;
        if (res.success) {
          const list = res.data.series || [];
          setSeries(list);
          setDirection(res.data.direction || 'lower');
          // 預設顯示第一個組別，可再勾選其他組別互相比較
          const listMains = [...new Set(list.map((s) => mainOf(s.name)))];
          setMains((prev) => {
            const kept = prev.filter((m) => listMains.includes(m));
            return kept.length > 0 ? kept : listMains.slice(0, 1);
          });
          if (res.data.message) setMessage(res.data.message);
        } else {
          setMessage(res.message || '統計查詢失敗');
        }
      })
      .catch((err) => {
        if (mounted) setMessage(err.response?.data?.message || '統計查詢失敗');
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [sport, event, gamesName]);

  const mainOptions = useMemo(
    () => [...new Set(series.map((s) => mainOf(s.name)))],
    [series]
  );

  // 分級選項：只列選取組別底下有的分級（一般賽事沒有分級，選單會隱藏）
  const clsOptions = useMemo(
    () =>
      [...new Set(
        series
          .filter((s) => mains.includes(mainOf(s.name)))
          .map((s) => clsOf(s.name))
          .filter(Boolean)
      )],
    [series, mains]
  );

  // 分級選項變動時修正選取值：保留仍有效的，全部失效則預設第一個
  useEffect(() => {
    setClsSel((prev) => {
      const kept = prev.filter((c) => clsOptions.includes(c));
      if (kept.length > 0) return kept;
      return clsOptions.length > 0 ? clsOptions.slice(0, 1) : [];
    });
  }, [clsOptions]);

  const activeSeries = useMemo(
    () =>
      series.filter((s) => {
        if (!mains.includes(mainOf(s.name))) return false;
        const cls = clsOf(s.name);
        return cls === '' || clsSel.includes(cls);
      }),
    [series, mains, clsSel]
  );

  const chartData = useMemo(() => {
    const years = [...new Set(activeSeries.flatMap((s) => s.points.map((p) => p.year)))].sort();
    return years.map((year) => {
      const row = { year };
      activeSeries.forEach((s) => {
        const point = s.points.find((p) => p.year === year);
        if (point) {
          row[s.name] = point[metric];
          // 該節點最佳成績的選手，tooltip 顯示與點擊跳轉查詢用
          row[`${s.name}__athlete`] = point.best_athlete || null;
        }
      });
      return row;
    });
  }, [activeSeries, metric]);

  // 點擊資料點時跳回查詢分頁，自動查該節點最佳成績的選手
  // recharts 各版本傳參順序不同，從參數中找出帶 payload 的物件
  const handleDotClick = (...args) => {
    const info = args.find((a) => a && typeof a === 'object' && a.payload);
    if (!info) return;
    const athlete = info.payload[`${info.dataKey}__athlete`];
    if (athlete && onSearchAthlete) onSearchAthlete(athlete);
  };

  const formatValue = direction === 'lower'
    ? formatSeconds
    : (v) => (v == null ? '-' : Number(v).toFixed(2));

  return (
    <Grid container spacing={3}>
      {/* 左欄：控制項 */}
      <Grid item xs={12} md={3}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            fullWidth
            label="賽事"
            value={gamesName}
            onChange={(e) => setGamesName(e.target.value)}
            size="small"
          >
            <MenuItem value="">全部賽事</MenuItem>
            {gamesNames.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            label="運動類別"
            value={sportOptions.includes(sport) ? sport : ''}
            onChange={(e) => setSport(e.target.value)}
            size="small"
          >
            {sportOptions.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            options={events}
            value={event}
            onChange={(e, v) => setEvent(v)}
            size="small"
            noOptionsText="無項目"
            renderInput={(params) => (
              <TextField {...params} label="項目" placeholder="選擇或輸入搜尋" />
            )}
          />
          <TextField
            select
            fullWidth
            label="組別（可複選比較）"
            value={mains}
            onChange={(e) => setMains(e.target.value)}
            size="small"
            disabled={series.length === 0}
            SelectProps={{
              multiple: true,
              renderValue: (value) => value.join('、'),
            }}
          >
            {mainOptions.map((m) => (
              <MenuItem key={m} value={m}>
                <Checkbox checked={mains.includes(m)} size="small" sx={{ p: 0.5, mr: 1 }} />
                {m}
              </MenuItem>
            ))}
          </TextField>
          {clsOptions.length > 0 && (
            <TextField
              select
              fullWidth
              label="分級（可複選比較）"
              value={clsSel}
              onChange={(e) => setClsSel(e.target.value)}
              size="small"
              SelectProps={{
                multiple: true,
                renderValue: (value) => value.join('、'),
              }}
            >
              {clsOptions.map((c) => (
                <MenuItem key={c} value={c}>
                  <Checkbox checked={clsSel.includes(c)} size="small" sx={{ p: 0.5, mr: 1 }} />
                  {c}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            fullWidth
            label="統計指標"
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            size="small"
          >
            <MenuItem value="best">最佳成績</MenuItem>
            <MenuItem value="avg">平均成績</MenuItem>
          </TextField>
          <Typography variant="body2" color="text.secondary">
            {direction === 'lower'
              ? '計時項目單位為秒，數值越低成績越好。'
              : '此項目數值越高成績越好。'}
            可勾選多個組別互相比較，例如公開對一般、男生對女生。
            點擊圖上資料點可跳轉查詢該筆最佳成績的選手。
          </Typography>
        </Box>
      </Grid>

      {/* 右欄：圖表 */}
      <Grid item xs={12} md={9}>
        {loading && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && message && <Alert severity="info">{message}</Alert>}

        {!loading && chartData.length > 0 && (
          <>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {event} 歷年{metric === 'best' ? '最佳成績' : '平均成績'}
          </Typography>
          <Box sx={{ width: '100%', height: 420 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 8, right: 24, bottom: 4, left: 8 }}>
                <CartesianGrid stroke="#eceff3" vertical={false} />
                <XAxis dataKey="year" tick={chartTextStyle} axisLine={{ stroke: '#d5dae1' }} tickLine={false} />
                <YAxis
                  domain={[
                    (dataMin) => Math.floor((dataMin - Math.max(0.3, dataMin * 0.02)) * 10) / 10,
                    (dataMax) => Math.ceil((dataMax + Math.max(0.3, dataMax * 0.02)) * 10) / 10,
                  ]}
                  tickFormatter={formatValue}
                  tick={chartTextStyle}
                  axisLine={false}
                  tickLine={false}
                  width={64}
                />
                <Tooltip
                  formatter={(value, name, item) => {
                    const athlete = item?.payload?.[`${name}__athlete`];
                    return [
                      `${formatValue(value)}${athlete ? `（最佳：${athlete}）` : ''}`,
                      name,
                    ];
                  }}
                  labelFormatter={(year) => `${year} 年（點擊資料點查詢選手）`}
                />
                <Legend wrapperStyle={{ fontSize: 13 }} />
                {activeSeries.map((s) => (
                  <Line
                    key={s.name}
                    type="monotone"
                    dataKey={s.name}
                    stroke={SERIES_COLORS[series.findIndex((x) => x.name === s.name) % SERIES_COLORS.length]}
                    strokeWidth={2}
                    connectNulls
                    dot={{ r: 3, strokeWidth: 2, fill: '#fff' }}
                    activeDot={{ r: 6, cursor: 'pointer', onClick: handleDotClick }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
          </>
        )}
      </Grid>
    </Grid>
  );
};

const StatsPanel = ({ onSearchAthlete }) => {
  const [mode, setMode] = useState('trend');
  const [statsSports, setStatsSports] = useState([]);
  const [allSports, setAllSports] = useState([]);
  const [gamesNames, setGamesNames] = useState([]);

  useEffect(() => {
    let mounted = true;
    athleteService
      .getStatsSports()
      .then((res) => {
        if (mounted && res.success) setStatsSports(res.data);
      })
      .catch(() => {});
    athleteService
      .getFilterOptions()
      .then((res) => {
        if (mounted && res.success) {
          setAllSports(res.data.sports);
          setGamesNames(res.data.games_names || []);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h5">統計分析</Typography>
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          onChange={(e, v) => v && setMode(v)}
        >
          <ToggleButton value="trend">項目成績趨勢</ToggleButton>
          <ToggleButton value="school">學校實力分析</ToggleButton>
          <ToggleButton value="county">縣市實力分析</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === 'trend' && (
        <EventTrend
          sports={statsSports}
          gamesNames={gamesNames}
          onSearchAthlete={onSearchAthlete}
        />
      )}
      {mode === 'school' && (
        <SchoolStrength allSports={allSports} onSearchAthlete={onSearchAthlete} />
      )}
      {mode === 'county' && (
        <CountyStrength
          allSports={allSports}
          gamesNames={gamesNames}
          onSearchAthlete={onSearchAthlete}
        />
      )}
    </Paper>
  );
};

export default StatsPanel;
